import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Image } from 'semantic-ui-react';
import { z } from 'zod';
import Footer from '../landing/footer';
import Header from '../landing/header';
import useScrollBehavior from '../landing/useScrollBehavior';
import HoneypotField from '../../components/form/honeypotField';
import useBotCheck from '../../hooks/useBotCheck';
import MailService from '../../service/mails';
import Alert from '../../utils/alert';
import './bookDemo.css';

const REQUEST_FAILED_MESSAGE =
    'We are sorry but we were not able to process your request. Please try again or send us a direct email.';

const formSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Invalid email address'),
    jobPosition: z.string().min(1, 'Job Position is required'),
    company: z.string().optional(),
    // Honeypot. Left unvalidated on purpose: a non-empty value must fail silently
    // rather than surface a validation error. See useBotCheck.
    website: z.string().optional(),
});

type FormSchemaType = z.infer<typeof formSchema>;

function BookDemo() {
    const location = useLocation();
    const navigate = useNavigate();
    const { scrollToTop, scrollToElement } = useScrollBehavior();
    const { interactionProps, isBotSubmission, recordSubmission } = useBotCheck({
        storageKey: 'bookDemo.lastSubmittedAt',
    });
    const [isSubmitted, setIsSubmitted] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<FormSchemaType>({
        defaultValues: {
            name: '',
            email: '',
            jobPosition: '',
            company: '',
            website: '',
        },
        resolver: zodResolver(formSchema),
    });

    useEffect(() => {
        const id = location.hash.replace('#', '');
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                let hasScrolled = false;
                if (id) {
                    hasScrolled = scrollToElement(id);
                }

                if (!id || !hasScrolled) {
                    scrollToTop();
                }
            });
        });
    }, [location.hash, scrollToElement, scrollToTop]);

    const handleSectionClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;

        e.preventDefault();
        navigate(`/book-demo#${sectionId}`, { replace: location.pathname === '/book-demo' });
    };

    const onSubmit = handleSubmit(async (data) => {
        if (isBotSubmission({
            honeypot: data.website,
            textFields: [data.name, data.jobPosition, data.company],
        })) {
            // Show the same confirmation a visitor sees, but send nothing. A bot that
            // is told it was rejected only teaches its operator what to change.
            setIsSubmitted(true);
            reset();
            return;
        }

        const wasSent = await new Promise<boolean>((resolve) =>
            MailService.sendUserRequest({
                clientEmail: data.email,
                clientName: data.name,
                description: `Job Position: ${data.jobPosition}, Company: ${data.company || 'Not specified'}`,
            },
                () => resolve(true),
                (error: { msg?: string }) => {
                    Alert.error(error?.msg || REQUEST_FAILED_MESSAGE);
                    resolve(false);
                })
        );

        if (!wasSent) {
            return;
        }

        recordSubmission();
        setIsSubmitted(true);
        reset();
    });

    return (
        <div id="app">
            <Header />
            <main id="main-content">
                <section className="booking__hero">
                    <div className="booking__hero-content">
                        <h1 className="booking__hero-title">Book a Demo</h1>

                        <p className="booking__hero-description">
                            Schedule a free demo to explore whether our tool meets your needs - whether you're investing
                            personally or sourcing data on behalf of a business.
                        </p>

                        <div className="booking__hero-buttons">
                            <Link
                                to="/book-demo#request-demo-form"
                                onClick={(e) => handleSectionClick(e, 'request-demo-form')}
                                className="sp-btn sp-btn--primary-inverted sp-btn--lg"
                            >
                                Request Demo
                            </Link>
                        </div>
                    </div>
                    <div className="booking__image">
                        <Image src={'/a.png'} />
                    </div>
                </section>

                <section id="request-demo-form" className="booking__form-section">
                    <div className="sp-container-xs">
                        <div className="sp-section-header">
                            <h2>Request a Demo</h2>
                            <div className="sp-divider"></div>
                            <p className="sp-section-description">
                                Fill in the form and we will contact you via email promptly.
                            </p>
                        </div>

                        <div className="sp-form-container">
                            <Form onSubmit={onSubmit} className="booking__form" {...interactionProps}>
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Form.Input
                                            disabled={isSubmitting}
                                            className="sp-form-input required"
                                            name="name"
                                            id="name"
                                            label="First and Last Name"
                                            placeholder="Jane Doe"
                                            value={value}
                                            onChange={(_, data) => onChange(data.value)}
                                            error={errors.name?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Form.Input
                                            disabled={isSubmitting}
                                            className="sp-form-input required"
                                            id="email"
                                            name="email"
                                            type="email"
                                            label="Email"
                                            placeholder="jane.doe@example.com"
                                            value={value}
                                            onChange={(_, data) => onChange(data.value)}
                                            error={errors.email?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="jobPosition"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Form.Input
                                            disabled={isSubmitting}
                                            className="sp-form-input required"
                                            id="job-position"
                                            name="jobPosition"
                                            type="text"
                                            label="Job Position"
                                            placeholder="Financial Investor"
                                            value={value}
                                            onChange={(_, data) => onChange(data.value)}
                                            error={errors.jobPosition?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="company"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <Form.Input
                                            disabled={isSubmitting}
                                            className="sp-form-input"
                                            id="company"
                                            name="company"
                                            type="text"
                                            label="Company"
                                            placeholder="Company Name"
                                            value={value}
                                            onChange={(_, data) => onChange(data.value)}
                                            error={errors.company?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="website"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <HoneypotField value={value ?? ''} onChange={onChange} />
                                    )}
                                />

                                <div className="sp-form-actions">
                                    <button
                                        type="submit"
                                        className="sp-btn sp-btn--primary sp-btn--lg"
                                        disabled={isSubmitting}
                                    >
                                        Request Demo
                                    </button>
                                </div>
                            </Form>

                            <div
                                className={`container-xs booking__confirmation ${
                                    isSubmitted
                                        ? 'booking__confirmation--visible'
                                        : 'booking__confirmation--hidden'
                                }`}
                            >
                                <div className="sp-section-header">
                                    <h2>Demo requested successfully!</h2>
                                    <p className="sp-section-description">
                                        Thank you. We will be in touch with you shortly to schedule your demo.
                                    </p>
                                    <p className="sp-section-description">
                                        In the meantime, feel free to explore the companies.
                                    </p>
                                </div>
                                <Link
                                    to="/companies"
                                    className="sp-btn sp-btn--primary-inverted sp-btn--lg"
                                >
                                    Explore Companies
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default BookDemo;
